import { useEffect, useRef, useState } from "react";
import { Wand2, Image as ImageIcon, Loader2, Download, X, Stamp, ChevronLeft, ChevronRight, Upload, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const POSITIONS = [
    { value: "bottom-right", label: "Bottom right" },
    { value: "bottom-left", label: "Bottom left" },
    { value: "top-right", label: "Top right" },
    { value: "top-left", label: "Top left" },
    { value: "center", label: "Centered" },
];

/* Vastra Studio: background-removal + watermark + shop-logo manager.
   One dialog, three tools, walks the owner through every item that needs polish. */
export default function StudioDialog({ open, onOpenChange, sarees = [], shop, onShopUpdate, onDone }) {
    const [tab, setTab] = useState("remove-bg");          // remove-bg | watermark
    const [idx, setIdx] = useState(0);
    const [busy, setBusy] = useState(false);
    const [resultImg, setResultImg] = useState(null);
    // Watermark settings
    const [wmUseLogo, setWmUseLogo] = useState(true);
    const [wmText, setWmText] = useState("");
    const [wmPosition, setWmPosition] = useState("bottom-right");
    const [wmOpacity, setWmOpacity] = useState(0.8);
    // Shop logo upload
    const [logoUploading, setLogoUploading] = useState(false);
    const logoFileRef = useRef(null);

    // Pool of items with images (any)
    const pool = sarees.filter((s) => (s.images?.length || s.ai_model_images?.length));
    const current = pool[idx];

    useEffect(() => {
        if (!open) return;
        setIdx(0); setResultImg(null);
    }, [open]);

    useEffect(() => {
        // Clear preview when switching item or tab
        setResultImg(null);
    }, [idx, tab]);

    const runRemoveBg = async () => {
        if (!current) return;
        setBusy(true); setResultImg(null);
        try {
            const { data } = await api.post(
                `/admin/sarees/${current.id}/studio/remove-bg`,
                { image_index: 0 },
                { timeout: 90000 },
            );
            setResultImg(data.image);
            toast.success("Background removed — saved as a new AI photo");
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Background removal failed");
        } finally {
            setBusy(false);
        }
    };

    const runWatermark = async () => {
        if (!current) return;
        if (wmUseLogo && !shop?.logo) {
            toast.error("Upload your shop logo first");
            return;
        }
        setBusy(true); setResultImg(null);
        try {
            const { data } = await api.post(
                `/admin/sarees/${current.id}/studio/watermark`,
                {
                    image_index: 0,
                    use_logo: wmUseLogo,
                    text: wmUseLogo ? null : (wmText || shop?.name || "Vastra AI"),
                    position: wmPosition,
                    opacity: wmOpacity,
                },
                { timeout: 30000 },
            );
            setResultImg(data.image);
            toast.success("Watermark applied (preview only — download to save)");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Watermark failed");
        } finally {
            setBusy(false);
        }
    };

    const uploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) {
            toast.error("Logo too large — max 4 MB");
            return;
        }
        setLogoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/shop/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onShopUpdate?.({ ...shop, logo: data.logo });
            toast.success("Logo saved");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
            setLogoUploading(false);
            if (logoFileRef.current) logoFileRef.current.value = "";
        }
    };

    const downloadResult = () => {
        if (!resultImg) return;
        const a = document.createElement("a");
        a.href = resultImg;
        a.download = `vastra-studio-${current?.id?.slice(0, 8)}.jpg`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    const downloadShopifyCsv = async () => {
        try {
            const res = await api.get("/admin/shopify-export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `shopify-${shop?.slug || "export"}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Shopify CSV downloaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
        }
    };

    const sourceImg = current?.images?.[0] || current?.ai_model_images?.[0];

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="studio-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                        <Wand2 className="w-5 h-5 text-brand-primary" strokeWidth={1.5} /> Vastra Studio
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">
                        Background removal · Watermark · Shopify export — get your catalog ready for any sales channel.
                    </DialogDescription>
                </DialogHeader>

                {/* Shop logo + Shopify CSV row */}
                <div className="ayraa-card p-3 flex flex-wrap items-center gap-3">
                    <div className="w-14 h-14 grid place-items-center bg-brand-subtle rounded-md overflow-hidden border border-brand-border shrink-0">
                        {shop?.logo ? (
                            <img src={shop.logo} alt="logo" className="w-full h-full object-contain" />
                        ) : (
                            <ImageIcon className="w-5 h-5 text-brand-soft" strokeWidth={1.4} />
                        )}
                    </div>
                    <div className="flex-1 min-w-[160px]">
                        <div className="ayraa-overline">Shop logo</div>
                        <div className="text-xs text-brand-muted">
                            PNG with transparent background works best · used by watermark tool
                        </div>
                    </div>
                    <label className="ayraa-btn-outline text-xs cursor-pointer" data-testid="studio-logo-upload">
                        {logoUploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                        {shop?.logo ? "Replace logo" : "Upload logo"}
                        <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                    </label>
                    <button className="ayraa-btn-outline text-xs" onClick={downloadShopifyCsv} data-testid="studio-shopify-csv">
                        <Download className="w-3.5 h-3.5 mr-1" /> Shopify CSV
                    </button>
                </div>

                {/* Tabs */}
                <div className="mt-2 flex gap-1 bg-brand-subtle p-1 rounded-md w-fit">
                    <button
                        type="button"
                        className={`text-xs px-3 py-1.5 rounded ${tab === "remove-bg" ? "bg-brand-paper shadow text-brand-primary" : "text-brand-muted"}`}
                        onClick={() => setTab("remove-bg")}
                        data-testid="studio-tab-remove-bg"
                    >
                        <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Remove background
                    </button>
                    <button
                        type="button"
                        className={`text-xs px-3 py-1.5 rounded ${tab === "watermark" ? "bg-brand-paper shadow text-brand-primary" : "text-brand-muted"}`}
                        onClick={() => setTab("watermark")}
                        data-testid="studio-tab-watermark"
                    >
                        <Stamp className="w-3.5 h-3.5 inline mr-1" /> Watermark
                    </button>
                </div>

                {pool.length === 0 ? (
                    <div className="py-10 text-center text-brand-muted">No items yet. Upload some photos first.</div>
                ) : !current ? null : (
                    <div className="grid sm:grid-cols-2 gap-4 mt-2">
                        {/* Source preview */}
                        <div>
                            <div className="ayraa-overline mb-1.5 flex items-center justify-between">
                                <span>Item ({idx + 1} / {pool.length})</span>
                                <div className="flex gap-1">
                                    <button
                                        className="ayraa-btn-ghost text-xs px-2 py-1"
                                        onClick={() => setIdx(Math.max(0, idx - 1))}
                                        disabled={idx === 0}
                                        data-testid="studio-prev"
                                    >
                                        <ChevronLeft className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        className="ayraa-btn-ghost text-xs px-2 py-1"
                                        onClick={() => setIdx(Math.min(pool.length - 1, idx + 1))}
                                        disabled={idx === pool.length - 1}
                                        data-testid="studio-next"
                                    >
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="aspect-[3/4] bg-brand-subtle rounded-md overflow-hidden border border-brand-border">
                                {sourceImg ? <img src={sourceImg} alt={current.title} className="w-full h-full object-cover" /> : null}
                            </div>
                            <div className="text-xs mt-1.5 text-brand-muted line-clamp-1">{current.title}</div>
                        </div>

                        {/* Tool panel + result */}
                        <div>
                            <div className="ayraa-overline mb-1.5">Result</div>
                            <div className="aspect-[3/4] bg-white rounded-md overflow-hidden border border-brand-border relative">
                                {busy ? (
                                    <div className="absolute inset-0 grid place-items-center bg-black/30 text-white">
                                        <div className="text-center">
                                            <Loader2 className="w-8 h-8 animate-spin mx-auto" strokeWidth={1.2} />
                                            <div className="text-xs mt-2">~10-30 seconds</div>
                                        </div>
                                    </div>
                                ) : null}
                                {resultImg ? (
                                    <img src={resultImg} alt="result" className="w-full h-full object-cover" data-testid="studio-result-img" />
                                ) : (
                                    <div className="w-full h-full grid place-items-center text-brand-soft text-xs px-4 text-center">
                                        Tap "Run" to preview the {tab === "remove-bg" ? "background-removed" : "watermarked"} image.
                                    </div>
                                )}
                            </div>

                            {/* Tool controls */}
                            {tab === "remove-bg" ? (
                                <div className="mt-3 space-y-2">
                                    <p className="text-xs text-brand-muted">
                                        Replaces the photo background with pure white — ideal for product cards.
                                        Adds a new entry under "AI photos" (your original photo is kept).
                                    </p>
                                    <button
                                        className="ayraa-btn-primary w-full"
                                        onClick={runRemoveBg}
                                        disabled={busy}
                                        data-testid="studio-run-remove-bg"
                                    >
                                        {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Removing…</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Remove background</>}
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-3 space-y-2">
                                    <div className="flex items-center gap-2 text-xs">
                                        <label className="flex items-center gap-1.5">
                                            <input
                                                type="checkbox"
                                                checked={wmUseLogo}
                                                onChange={(e) => setWmUseLogo(e.target.checked)}
                                                data-testid="studio-wm-uselogo"
                                            />
                                            Use shop logo
                                        </label>
                                        {!shop?.logo ? <span className="text-brand-primary text-[10px]">(upload logo first)</span> : null}
                                    </div>
                                    {!wmUseLogo ? (
                                        <input
                                            className="ayraa-input text-sm"
                                            placeholder={shop?.name || "Watermark text"}
                                            value={wmText}
                                            onChange={(e) => setWmText(e.target.value)}
                                            data-testid="studio-wm-text"
                                        />
                                    ) : null}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="ayraa-overline">Position</label>
                                            <select
                                                className="ayraa-input mt-1 text-sm"
                                                value={wmPosition}
                                                onChange={(e) => setWmPosition(e.target.value)}
                                                data-testid="studio-wm-position"
                                            >
                                                {POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Opacity ({Math.round(wmOpacity * 100)}%)</label>
                                            <input
                                                type="range"
                                                min="0.2"
                                                max="1"
                                                step="0.05"
                                                value={wmOpacity}
                                                onChange={(e) => setWmOpacity(parseFloat(e.target.value))}
                                                className="w-full mt-2"
                                                data-testid="studio-wm-opacity"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="ayraa-btn-primary w-full"
                                        onClick={runWatermark}
                                        disabled={busy}
                                        data-testid="studio-run-watermark"
                                    >
                                        {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Stamping…</> : <><Stamp className="w-4 h-4 mr-1.5" /> Apply watermark</>}
                                    </button>
                                </div>
                            )}

                            {resultImg ? (
                                <button className="ayraa-btn-outline w-full mt-2" onClick={downloadResult} data-testid="studio-download-result">
                                    <Download className="w-4 h-4 mr-1.5" /> Download
                                </button>
                            ) : null}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
